import React from 'react';
import { Loader2 } from 'lucide-react';

// Stat Card
export const StatCard = ({ title, value, subtitle, icon: Icon, color = 'emerald', trend }) => {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    violet: 'bg-violet-50 text-violet-600 border-violet-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    teal: 'bg-teal-50 text-teal-600 border-teal-200'
  };
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          {trend && <p className={`text-xs font-medium mt-1 ${trend > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {trend > 0 ? '▲' : '▼'} {Math.abs(trend)}% from last month
          </p>}
        </div>
        <div className={`p-3 rounded-xl border ${colorMap[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

// Progress Bar
export const ProgressBar = ({ value, label, color = 'emerald', showValue = true }) => {
  const colorMap = {
    emerald: 'bg-emerald-500',
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    violet: 'bg-violet-500'
  };
  return (
    <div className="space-y-1.5">
      {label && <div className="flex justify-between text-sm">
        <span className="text-gray-600 font-medium">{label}</span>
        {showValue && <span className="text-gray-500">{value}%</span>}
      </div>}
      <div className="w-full bg-gray-100 rounded-full h-2.5">
        <div className={`h-2.5 rounded-full ${colorMap[color]} transition-all duration-700`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
};

// Badge
export const Badge = ({ children, color = 'gray' }) => {
  const colorMap = {
    gray: 'bg-gray-100 text-gray-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    violet: 'bg-violet-100 text-violet-700',
    teal: 'bg-teal-100 text-teal-700'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorMap[color]}`}>{children}</span>;
};

// Status badge with smart color
export const StatusBadge = ({ status }) => {
  const map = {
    scheduled: { color: 'blue', label: 'Scheduled' },
    confirmed: { color: 'teal', label: 'Confirmed' },
    'in-progress': { color: 'amber', label: 'In Progress' },
    completed: { color: 'emerald', label: 'Completed' },
    cancelled: { color: 'red', label: 'Cancelled' },
    'no-show': { color: 'gray', label: 'No Show' },
    available: { color: 'emerald', label: 'Available' },
    occupied: { color: 'red', label: 'Occupied' },
    maintenance: { color: 'amber', label: 'Maintenance' },
    reserved: { color: 'blue', label: 'Reserved' },
    present: { color: 'emerald', label: 'Present' },
    absent: { color: 'red', label: 'Absent' },
    'half-day': { color: 'amber', label: 'Half Day' },
    leave: { color: 'violet', label: 'Leave' }
  };
  const s = map[status] || { color: 'gray', label: status };
  return <Badge color={s.color}>{s.label}</Badge>;
};

// Loading Spinner
export const Spinner = ({ size = 'md' }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' };
  return <Loader2 className={`${sizes[size]} animate-spin text-emerald-500`} />;
};

// Loading state
export const LoadingState = ({ message = 'Loading...' }) => (
  <div className="flex flex-col items-center justify-center py-12 gap-3">
    <Spinner size="lg" />
    <p className="text-gray-500 text-sm">{message}</p>
  </div>
);

// Empty state
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
    {Icon && <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
      <Icon className="w-7 h-7 text-gray-400" />
    </div>}
    <div>
      <h3 className="text-gray-700 font-semibold">{title}</h3>
      {description && <p className="text-gray-400 text-sm mt-1">{description}</p>}
    </div>
    {action}
  </div>
);

// Card wrapper
export const Card = ({ children, className = '', title, subtitle, action }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-50">
        <div>
          <h3 className="text-gray-800 font-semibold">{title}</h3>
          {subtitle && <p className="text-gray-400 text-xs mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
    )}
    <div className="p-5">{children}</div>
  </div>
);

// Button
export const Button = ({ children, onClick, variant = 'primary', size = 'md', loading, disabled, className = '', type = 'button', ...props }) => {
  const variants = {
    primary: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
    danger: 'bg-red-500 hover:bg-red-600 text-white',
    outline: 'border border-gray-200 hover:bg-gray-50 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-600'
  };
  const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-4 py-2 text-sm', lg: 'px-6 py-3 text-base' };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center gap-2 font-medium rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {loading && <Spinner size="sm" />}
      {children}
    </button>
  );
};

// Input
export const Input = ({ label, error, className = '', ...props }) => (
  <div className="space-y-1">
    {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
    <input
      className={`w-full px-3 py-2.5 border rounded-xl text-sm outline-none transition-all
        focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
        ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}
        ${className}`}
      {...props}
    />
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

// Select
export const Select = ({ label, error, children, className = '', ...props }) => (
  <div className="space-y-1">
    {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
    <select
      className={`w-full px-3 py-2.5 border rounded-xl text-sm outline-none transition-all
        focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
        border-gray-200 bg-gray-50 hover:border-gray-300 ${className}`}
      {...props}
    >
      {children}
    </select>
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

// Modal
export const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizes = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${sizes[size]} max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg text-gray-500">✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

// Avatar
export const Avatar = ({ name, size = 'md', color = 'emerald' }) => {
  const initials = name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'AC';
  const sizes = { sm: 'w-7 h-7 text-xs', md: 'w-9 h-9 text-sm', lg: 'w-12 h-12 text-base' };
  const colors = { emerald: 'from-emerald-500 to-teal-600', blue: 'from-blue-500 to-indigo-600', violet: 'from-violet-500 to-purple-600' };
  return <div className={`${sizes[size]} bg-gradient-to-br ${colors[color]} rounded-full flex items-center justify-center text-white font-bold`}>{initials}</div>;
};

export const DoshaTag = ({ dosha }) => {
  const colors = { Vata: 'bg-sky-100 text-sky-700', Pitta: 'bg-orange-100 text-orange-700', Kapha: 'bg-green-100 text-green-700', Tridosha: 'bg-purple-100 text-purple-700' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[dosha] || 'bg-gray-100 text-gray-600'}`}>{dosha}</span>;
};

// GMeet Button with Time-Gated Access
export const JoinGMeetButton = ({ appointment, size = 'sm', className = '' }) => {
  if (!appointment) return null;

  const meetingUrl = appointment.meetingUrl || 'https://meet.google.com/jnc-wryp-xrs';
  const apptDate = appointment.date ? new Date(appointment.date) : null;
  const startTime = appointment.startTime;
  const endTime = appointment.endTime;

  if (!apptDate || !startTime) {
    return (
      <a href={meetingUrl} target="_blank" rel="noopener noreferrer"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-all ${className}`}>
        🎥 Join GMeet
      </a>
    );
  }

  const now = new Date();
  const [sH, sM] = String(startTime).split(':').map(Number);
  const start = new Date(apptDate);
  start.setHours(sH || 0, sM || 0, 0, 0);

  let end;
  if (endTime) {
    const [eH, eM] = String(endTime).split(':').map(Number);
    end = new Date(apptDate);
    end.setHours(eH || 0, eM || 0, 0, 0);
    if (end <= start) end.setDate(end.getDate() + 1);
  } else {
    end = new Date(start.getTime() + (appointment.duration || 60) * 60000);
  }

  // Opens 5 mins before startTime
  const joinWindowOpensAt = new Date(start.getTime() - 5 * 60000);
  const isBefore = now < joinWindowOpensAt;
  const isAfter = now > end || appointment.status === 'completed' || appointment.status === 'cancelled';
  const isActive = !isBefore && !isAfter;

  const timeStr = start.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  const dateStr = start.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });

  const handleClick = (e) => {
    if (isBefore) {
      e.preventDefault();
      const toastMsg = `GMeet link is locked. It will activate at ${timeStr} on ${dateStr}.`;
      alert(toastMsg);
      return;
    }
    if (isAfter) {
      e.preventDefault();
      alert('This appointment session has ended.');
      return;
    }
    window.open(meetingUrl, '_blank', 'noopener,noreferrer');
  };

  if (isAfter) {
    return (
      <button disabled className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-gray-100 text-gray-400 cursor-not-allowed ${className}`}>
        🚫 Session Ended
      </button>
    );
  }

  if (isBefore) {
    return (
      <button onClick={handleClick} title={`Link activates at ${timeStr}`}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-all ${className}`}>
        🔒 GMeet Opens at {timeStr}
      </button>
    );
  }

  return (
    <button onClick={handleClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md animate-pulse transition-all ${className}`}>
      🎥 Join GMeet Session
    </button>
  );
};

// ── Licensed Doctor Badge ──────────────────────────────────────────────────
export const LicensedDoctorBadge = ({ doctor, onClick, showIconOnly = false }) => {
  const profile = doctor?.doctorProfile || {};
  const status = profile.licenseStatus || (profile.licenseNumber ? 'approved' : 'none');

  if (status === 'approved') {
    if (showIconOnly) {
      return (
        <button onClick={onClick} title="Verified Licensed Practitioner" type="button"
          className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold hover:scale-110 transition-transform">
          ✓
        </button>
      );
    }
    return (
      <button onClick={onClick} type="button" title="Click to view Digital License Card"
        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-xs hover:shadow-md hover:scale-105 transition-all cursor-pointer">
        <span>🛡️</span> Licensed Doctor
      </button>
    );
  }

  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
        ⏳ Verification Pending
      </span>
    );
  }

  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700 border border-rose-200">
        ⚠️ Unverified Practitioner
      </span>
    );
  }

  return null;
};

// ── Digital Medical License Card Modal ──────────────────────────────────────
export const DoctorLicenseModal = ({ isOpen, onClose, doctor, onAdminApprove, onAdminReject, actionLoading }) => {
  if (!isOpen || !doctor) return null;

  const profile = doctor.doctorProfile || {};
  const status = profile.licenseStatus || (profile.licenseNumber ? 'approved' : 'none');
  const isApproved = status === 'approved';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Digital Medical Practitioner License" size="md">
      <div className="space-y-5">
        {/* Certificate Card Header */}
        <div className={`relative overflow-hidden rounded-2xl p-6 text-white shadow-xl ${isApproved ? 'bg-gradient-to-br from-emerald-700 via-teal-800 to-cyan-900 border-2 border-emerald-400/30' : 'bg-gradient-to-br from-amber-700 via-orange-800 to-amber-900'}`}>
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10 blur-xl pointer-events-none" />
          
          <div className="flex items-center justify-between border-b border-white/20 pb-4 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🏛️</span>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-200">Ayurvedic Medical Council</p>
                <p className="text-xs text-white/80">{profile.medicalCouncil || 'Tamil Nadu Board of Indian Medicine'}</p>
              </div>
            </div>
            {isApproved ? (
              <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-emerald-400 text-emerald-950 uppercase tracking-widest shadow-md">
                ✓ VERIFIED LICENSED
              </span>
            ) : (
              <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-amber-300 text-amber-950 uppercase tracking-widest">
                PENDING REVIEW
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center text-white text-2xl font-black shadow-inner flex-shrink-0">
              {doctor.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'DR'}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-white truncate">{doctor.name}</h2>
              <p className="text-emerald-100 text-xs font-medium">{profile.qualification || 'BAMS, MD (Ayurveda)'} • {profile.specialization || 'Ayurvedic Therapist'}</p>
              <p className="text-white/70 text-xs mt-1">Exp: {profile.experience || 5}+ Years Clinical Experience</p>
            </div>
          </div>

          {/* License Number Banner */}
          <div className="mt-5 pt-4 border-t border-white/20 grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-white/60 uppercase font-semibold text-[10px] tracking-wider">Registration License No.</p>
              <p className="font-mono font-bold text-sm text-amber-200">{profile.licenseNumber || 'TN-AYU-2012-1234'}</p>
            </div>
            <div>
              <p className="text-white/60 uppercase font-semibold text-[10px] tracking-wider">State Board Council</p>
              <p className="font-semibold text-white">{profile.licenseState || 'Tamil Nadu Board of Indian Medicine'}</p>
            </div>
          </div>
        </div>

        {/* Verification Metadata */}
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-xs space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-gray-500">Official Practitioner ID:</span>
            <span className="font-mono font-semibold text-gray-700">{doctor._id}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-500">Assigned Medical Center:</span>
            <span className="font-semibold text-gray-700">{profile.assignedCenter?.name || 'Avartana Care Main Center'}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-500">Digital Seal Standard:</span>
            <span className="text-emerald-700 font-semibold">ISO-27001 Medical Practitioner Standard</span>
          </div>
        </div>

        {/* Admin Approval Actions */}
        {onAdminApprove && !isApproved && (
          <div className="pt-3 border-t flex items-center justify-end gap-3">
            <Button variant="danger" size="sm" loading={actionLoading} onClick={onAdminReject}>
              Reject Application
            </Button>
            <Button variant="primary" size="sm" loading={actionLoading} onClick={onAdminApprove} className="bg-emerald-600 hover:bg-emerald-700">
              ✓ Approve &amp; Issue License
            </Button>
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close Card
          </Button>
        </div>
      </div>
    </Modal>
  );
};


